<template>
  <div>
    <RadioGroupRoot v-if="hasInited" v-model="paymentMethod">
      <template v-for="method in paymentMethods" :key="method.value">
        <RadioGroupItem
          :value="method.value"
          class="flex items-center justify-center gap-5"
          aria-label="Select payment method"
        >
          <Box class="flex h-9 w-9 items-center justify-center bg-white">
            <RadioGroupIndicator as-child>
              <IconsCheckmark class="h-3.5 w-3.5 stroke-[4]" />
            </RadioGroupIndicator>
          </Box>
          <label class="text-[15px] font-bold">
            <img
              aria-hidden
              :src="method.icon"
              class="mr-2 inline-block h-8 w-8"
            />
            {{ method.name }}
          </label>
        </RadioGroupItem>
        <Separator
          class="my-4 w-full border-b border-dashed border-navy"
          orientation="horizontal"
        />
      </template>
    </RadioGroupRoot>
    <template v-else>
      <div class="flex flex-col">
        <div class="flex items-center gap-5">
          <div class="h-9 w-9 animate-pulse rounded-lg bg-zinc-300" />
          <div class="h-8 w-40 animate-pulse rounded-lg bg-zinc-300" />
        </div>
        <Separator
          class="my-4 flex-1 border-b border-dashed border-navy"
          orientation="horizontal"
        />
        <div class="flex items-center gap-5">
          <div class="h-9 w-9 animate-pulse rounded-lg bg-zinc-300" />
          <div class="h-8 w-32 animate-pulse rounded-lg bg-zinc-300" />
        </div>
      </div>
      <Separator
        class="my-4 border-b border-dashed border-navy"
        orientation="horizontal"
      />
    </template>

    <div
      v-show="paymentMethod === 'card' || paymentMethod === 'ach'"
      class="mb-4"
    >
      <h4 v-if="paymentMethod === 'card'" class="font-bold text-gray-500">
        Card details
      </h4>
      <h4 v-else-if="paymentMethod === 'ach'" class="font-bold text-gray-500">
        ACH details
      </h4>
      <div v-show="hasRenderedCard" id="stripe-card-element" class="w-full">
        <PigLoading class="mx-auto my-10" />
      </div>
      <div v-if="!hasRenderedCard" class="w-full">
        <PigLoading class="mx-auto my-10" />
      </div>
    </div>

    <Box class="bg-white p-4">
      <dl class="space-y-4 text-[15px] leading-[18px] tracking-tight">
        <div class="flex items-center justify-between">
          <dt>Your donation</dt>
          <dd>{{ currencyFormatter.format(donationAmount) }}</dd>
        </div>
        <div v-if="tip" class="flex items-center justify-between">
          <dt>Tip</dt>
          <dd>{{ currencyFormatter.format(tip) }}</dd>
        </div>
        <div v-if="processingFee" class="flex items-center justify-between">
          <dt>Visa/Mastercard fee</dt>
          <dd>{{ currencyFormatter.format(processingFee) }}</dd>
        </div>
        <div v-if="ddcFee" class="flex items-center justify-between">
          <dt>DDC fee</dt>
          <dd>{{ currencyFormatter.format(ddcFee) }}</dd>
        </div>

        <Separator class="w-full border-b border-dashed border-navy" />

        <div class="flex items-center justify-between font-bold">
          <dt>Total</dt>
          <dd>{{ currencyFormatter.format(total) }}</dd>
        </div>
      </dl>
    </Box>

    <Separator class="my-6 w-full border-b border-dashed border-navy" />

    <div class="flex items-center gap-2">
      <Button
        class="!h-[42px] !w-[42px]"
        variant="icon"
        :disabled="isLoading"
        @click="$emit('back')"
      >
        <IconsArrowRight class="h-[20px] w-[20px] rotate-180" />
      </Button>
      <Button
        v-if="
          !paymentMethod || paymentMethod === 'card' || paymentMethod === 'ach'
        "
        class="w-full"
        :disabled="!paymentMethod || isLoading"
        variant="primary-flat"
        @click="
          confirmCardPayment({
            firstName: self!.firstName,
            lastName: self!.lastName,
            email: self!.email,
            phone: self!.phone ?? '',
            city: self!.city,
            state: self!.state,
            country: self!.country,
            postalCode: self!.postalCode ?? '',
          })
        "
      >
        <template v-if="!paymentMethod">Select a payment method</template>
        <template v-else-if="isLoading">
          <svg
            class="mx-auto h-[18px] w-[18px] animate-spin fill-gray-600 text-gray-200"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
        </template>
        <template v-else>Donate</template>
      </Button>
      <div
        v-show="
          (paymentMethod === 'google-pay' || paymentMethod === 'apple-pay') &&
          !isLoading
        "
        id="stripe-wallet-element"
        class="flex-1 self-stretch rounded bg-black"
      />
      <div
        v-if="
          (paymentMethod === 'google-pay' || paymentMethod === 'apple-pay') &&
          isLoading
        "
        class="relative flex flex-1 items-center justify-center self-stretch rounded bg-black"
      >
        <svg
          class="h-[18px] w-[18px] animate-spin fill-gray-600 text-gray-200"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { toast } from "vue3-toastify";
import { useSelf } from "~/domains/member/composables";
import { useCreateDonationMutation } from "~/domains/member/mutations";
import {
  useStripeCheckout,
  type PaymentMethod,
} from "~/domains/signup/composables/useStripeCheckout";
import type { Campaign } from "~/lib/campaigns";

const props = defineProps<{
  donation: {
    campaignId: Campaign;
    type: "one-time" | "subscription";
    amount: number;
    tip: number;
  };
}>();
const emit = defineEmits<{ (e: "back" | "success"): void }>();

const { data: self } = useSelf();

const paymentMethod = ref<PaymentMethod>();

const { donation } = toRefs(props);

const donationAmount = computed(() => {
  if (self.value?.type === "partner") return donation.value.amount * 0.9;
  return donation.value.amount;
});

const processingFee = computed(() => {
  if (self.value?.type === "partner") return 0;
  const subtotal = donation.value.tip + donation.value.amount;
  const gross = (subtotal + 0.3) / (1 - 0.029);
  const fee = gross - subtotal;
  return fee;
});

const tip = computed(() => {
  if (self.value?.type === "partner") return 0;
  return donation.value.tip;
});

const ddcFee = computed(() => {
  if (self.value?.type === "partner") return donation.value.amount * 0.1;
  return 0;
});

const total = computed(() => {
  if (self.value?.type === "partner") return donation.value.amount;
  return donation.value.amount + donation.value.tip + processingFee.value;
});

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

const { mutate, isPending } = useCreateDonationMutation();

const {
  init,
  renderCardElements,
  hasInited,
  paymentMethods,
  confirmCardPayment,
  isConfirming,
  hasRenderedCard,
} = useStripeCheckout({
  onError: (message) => {
    toast.error(message);
  },
  onSuccess: async (data) =>
    mutate(
      {
        setupIntentId: data.setupIntentId,
        campaignId: donation.value.campaignId,
        oneTime: donation.value.type === "one-time",
        amount: donation.value.amount,
        processingFee: processingFee.value,
        tip: donation.value.tip,
        isACH: paymentMethod.value === "ach",
      },
      {
        onSuccess: () => emit("success"),
        onError: (error) => toast.error(error.message),
      },
    ),
  ach: () => self.value?.type === "partner",
  selectedPaymentMethod: paymentMethod,
});

onBeforeMount(() => {
  init({
    totalAmount: total.value,
    cardContainerId: "stripe-card-element",
    walletContainerId: "stripe-wallet-element",
  });
});

watch(paymentMethod, () => {
  renderCardElements({
    containerElementId: "stripe-card-element",
  });
});

const isLoading = computed(() => isPending.value || isConfirming.value);
</script>
